import { FontWeights, IPersonaSharedProps, Link, NeutralColors, Persona, PersonaPresence, PersonaSize, Separator, Stack } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import MenuList from "../MenuList";
import { ButtonStyle } from "@/styles/buttonStyles";
import { getLocalStorage, getMSALInfo, getParameter, setLocalStorage } from "@/utilities/common";
import { customrOption } from "@BZA/Account/Customer/customerOption";
import { useNavigate } from "react-router-dom";
import SearchBox from "@/components/SearchBox";
import { CIDSeachBoxProps } from "@/utilities/searchProps";
import eventBus from "@/utilities/eventBus";
import { observer } from "@/utilities/storeHook";
import store from "@/store";
import api from "@/api";
import styles from "./styles/buttonStyles.modules.less";
import { getBasePath } from "@/utilities/function";
import { useTranslation } from "react-i18next";

export interface IButtonExampleProps {
  // These are set based on the toggles shown above the examples (not needed in real code)
  disabled?: boolean;
  checked?: boolean;
}

const PanelContent = () => {
  const navigate = useNavigate();
  const routerParamsCID = getParameter("cid");
  const [MSALInfo, setMSALInfo] = useState<any>();
  const [examplePerson, setExamplePersona] = useState<IPersonaSharedProps>();
  const [menuItems, setMenuItems] = useState<any>();
  const [__, setSearchValue] = useState<any>();
  const [liveMessage, setLiveMessage] = useState("");
  const { userStore } = store;
  const { t } = useTranslation();
  const setMenuItemsWithAnnouncement = (items: any) => {
    setMenuItems(items);
    const count = Array.isArray(items) ? items.length : 0;
    setTimeout(() => {
      setLiveMessage(count === 0
        ? t("Basics.noSearchResults")
        : t("Basics.searchResultsCount", { count }));
    }, 500);
  };
  useEffect(() => {
    if (routerParamsCID) {
      api.Customer.CIDlist({
        email: getMSALInfo()?.accountUsername,
      }).then((res: any) => {
        setMenuItems(res.customers);
        setLocalStorage("customers", res);
      });
    }

    if (customrOption.getCustomers() !== undefined) {
      setMenuItems(customrOption.getCustomers().customers);
    }

    if (routerParamsCID) {
      const currentCustomerInfo = customrOption.getCustomers()?.customers?.filter((item: any) => item?.customerId === Number(routerParamsCID))[0];
      if (currentCustomerInfo?.customerStatus === 0) {
        setLocalStorage("createdCustomer", currentCustomerInfo);
        navigate("/giveCIDAndUID");
      } else if (currentCustomerInfo?.customerStatus !== 0 && currentCustomerInfo?.numberOfAccounts === 0) {
        setLocalStorage("currentCID", currentCustomerInfo?.customerId);
        const newBasePath = getBasePath(Number(currentCustomerInfo?.productType), Number(currentCustomerInfo?.primaryPurpose), "/leadgen_createAccount", "/createAccount");
        navigate(newBasePath);
      }
    }
    return () => {
      setMenuItems([]);
    };
  }, [routerParamsCID]);

  const getMsalInfos = (info?: any) => {
    const currentInfo = info || JSON.parse(getLocalStorage("userInfo") || "{}");
    setMSALInfo(currentInfo);
    if (currentInfo.givenName) {
      setExamplePersona({
        text: currentInfo?.displayName || currentInfo?.accountUsername,
        secondaryText: currentInfo?.accountUsername,
        showSecondaryText: true,
      });
    } else {
      setExamplePersona({
        text: currentInfo.userPrincipalName || currentInfo?.accountUsername,
        secondaryText: currentInfo?.accountUsername,
        showSecondaryText: true,
      });
    }
  };
  useEffect(() => {
    getMsalInfos();
    eventBus.off("getCurrentUserInfo");
    eventBus.on("getCurrentUserInfo", ({ info }) => {
      getMsalInfos(info);
    });
  }, [userStore.isGotUserInfo]);
  const handleLogout = async () => {
    navigate("/");
    localStorage.clear();
  };

  const focusVisibleLinkStyles = {
    root: {
      selectors: {
        ":focus-visible": {
          outline: "none !important",
          border: "none !important",
          boxShadow: "inset 0 0 0 1px #323130 !important",
          padding: 2,
          textDecoration: "none",
        },
        "@media (forced-colors: active)": {
          "&:focus, &:focus-visible": {
            boxShadow: "inset 0 0 0 2px Highlight !important",
          }
        }
      }
    }
  } as const;

  return (
    <div className={styles.customer_box}>
      <div>
        <Persona
          {...examplePerson}
          text={MSALInfo?.givenName !== "" ? MSALInfo?.displayName : MSALInfo?.userPrincipalName || MSALInfo?.accountUsername}
          size={PersonaSize.size48}
          presence={PersonaPresence.none}
          imageAlt={MSALInfo?.givenName || MSALInfo?.displayName}
          tabIndex={0}
          style={{ gap: "16px", }}
          styles={{
            root: {
              gap: 16
            },
            details: {
              padding: 0,
            },
            primaryText: {
              fontWeight: FontWeights.bold,
              color: NeutralColors.black
            },
            secondaryText: {
              color: NeutralColors.black,
            }
          }}
        />
        <Link
          underline={false}
          tabIndex={0}
          styles={{
            root: {
              marginLeft: 64,
              selectors: focusVisibleLinkStyles.root.selectors,
            }
          }}
          onClick={() => handleLogout()}
        >
          {t("Basics.Logout")}
        </Link>
      </div>
      {routerParamsCID && menuItems
        ? <>
          <Separator styles={{ root: { margin: "12px 0 7px 0" } }} />
          <div className={styles.cid_box}>
            <SearchBox item={CIDSeachBoxProps(setMenuItemsWithAnnouncement, setSearchValue, t)} />
            <div className={styles.cid_container} style={{ gap: "16px" }}>
              <Stack horizontal horizontalAlign="space-between">
                <span className={styles.cid_title}>{t("Basics.ManageCustomers")}</span>
                <Link
                  className={ButtonStyle.LinkButton}
                  tabIndex={0}
                  styles={focusVisibleLinkStyles}
                  onClick={() => {
                    navigate("/customer");
                  }}
                >
                  {t("Basics.ViewAll")}
                </Link>
              </Stack>
              <MenuList
                menuItems={menuItems}
                menulistStyle={{
                  marginLeft: -24,
                  marginRight: -24,
                  flexGrow: 1,
                  overflow: "hidden auto",
                }}
              />
            </div>
          </div>
        </>
        : null}
      <span
        aria-live="polite"
        style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap" }}
      >
        {liveMessage}
      </span>
    </div>
  );
};
export default observer(PanelContent);
